import {useNavigate, useParams} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {GuestCategoryService} from "crqlar-react-common/dist/services/profile/guestCategory.service";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {GuestCategory} from "crqlar-react-common/dist/models/profile/guestCategory.model";
import {
    DataFilterOperator,
    DataTypeOperator,
    FilterGroup,
    FilterRule
} from "crqlar-react-common/dist/models/audience/filter.model";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {getErrorMessage} from "crqlar-react-common/dist/utils/http.util";
import {serverTableQueryKey} from "../../common/serverTable";
import {
    GUEST_CATEGORY_MAX_BOXES,
    GUEST_CATEGORY_MAX_RULES_PER_BOX,
    GUEST_CATEGORY_MAX_VALUE,
    GUEST_CATEGORY_METRICS,
    GUEST_CATEGORY_OPERATORS,
    isValidGuestCategoryFilter,
    isValidGuestCategoryRule,
    newGuestCategoryBox,
    newGuestCategoryFilter,
    newGuestCategoryRule
} from "../../guestCategories/guestCategoryRules";
import {GUEST_CATEGORIES_QUERY_KEY, useGuestCategories} from "../../guestCategories/useGuestCategories";

/**
 * Settings record for one guest category, the Age categories page's twin: shared settings header, property
 * rows for the basics, danger zone. The rules block repeats the audience dialog's fixed shape - boxes are
 * alternatives, the rules inside a box all apply - with one grid line per row, metric › comparison › value › ⋮.
 */

const ROW_HEIGHT = 40
const ROW_COLUMNS = 'minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1fr) 34px'

//region Rule row

interface RuleRowProps {
    rule: FilterRule
    /** Element id prefix, unique across boxes. */
    idPrefix: string
    disabled: boolean
    setRule: (rule: FilterRule) => void
    onDuplicate: () => void
    onDelete: () => void
}

const RuleRow = ({rule, idPrefix, disabled, setRule, onDuplicate, onDelete}: RuleRowProps) => {
    const {t} = useTranslation()
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
    const closeMenu = () => setMenuAnchor(null)
    const value = rule.longValues?.[0]

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: ROW_COLUMNS,
                columnGap: 1,
                alignItems: 'center',
                minHeight: ROW_HEIGHT,
            }}
        >
            <Select
                id={`${idPrefix}_metric`}
                size={'small'}
                disabled={disabled}
                value={rule.field ?? ''}
                inputProps={{'aria-label': t('Metric')}}
                onChange={event => setRule({...rule, field: event.target.value})}
                sx={{minWidth: 0}}
            >
                {GUEST_CATEGORY_METRICS.map(metric => (
                    <MenuItem key={metric.value} value={metric.value}>
                        {t(metric.label)}
                    </MenuItem>
                ))}
            </Select>

            <Select
                id={`${idPrefix}_operator`}
                size={'small'}
                disabled={disabled}
                value={rule.operator ?? ''}
                inputProps={{'aria-label': t('Comparison')}}
                onChange={event => setRule({...rule, operator: event.target.value as DataTypeOperator})}
                sx={{minWidth: 0}}
            >
                {GUEST_CATEGORY_OPERATORS.map(operator => (
                    <MenuItem key={operator} value={operator}>
                        {t(`Data type operator ${operator}`)}
                    </MenuItem>
                ))}
            </Select>

            <TextField
                id={`${idPrefix}_value`}
                size={'small'}
                hiddenLabel
                type={'number'}
                disabled={disabled}
                value={value ?? ''}
                error={value != null && !isValidGuestCategoryRule(rule)}
                inputProps={{min: 0, max: GUEST_CATEGORY_MAX_VALUE, step: 1, 'aria-label': t('Value')}}
                onChange={event => setRule({
                    ...rule,
                    longValues: [event.target.value === '' ? undefined : Number(event.target.value)],
                })}
                sx={{minWidth: 0}}
            />

            <IconButton
                size={'small'}
                aria-label={t('Rule options')}
                disabled={disabled}
                onClick={event => setMenuAnchor(event.currentTarget)}
            >
                <MoreVertIcon/>
            </IconButton>

            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
                <MenuItem onClick={() => {
                    onDuplicate()
                    closeMenu()
                }}>
                    <ListItemIcon><ContentCopyIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{t('Duplicate')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    onDelete()
                    closeMenu()
                }} sx={{color: 'error.main'}}>
                    <ListItemIcon><DeleteIcon fontSize={'small'} color={'error'}/></ListItemIcon>
                    <ListItemText>{t('Delete rule')}</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    )
}

//endregion Rule row

//region Rule box

interface RuleBoxProps {
    box: FilterGroup
    index: number
    disabled: boolean
    setBox: (box: FilterGroup) => void
    /** The last rule went: the page drops the box. */
    onEmpty: () => void
}

const RuleBox = ({box, index: boxIndex, disabled, setBox, onEmpty}: RuleBoxProps) => {
    const {t} = useTranslation()
    const rules = box.filterRules ?? []

    const setRules = (nextRules: FilterRule[]) =>
        nextRules.length === 0 ? onEmpty() : setBox({...box, filterRules: nextRules})

    return (
        <Paper variant={'outlined'} sx={{p: 1.5}}>
            <Typography variant={'subtitle2'} color={'text.secondary'} sx={{mb: 1}}>
                {t('WHERE')}
            </Typography>

            <Stack gap={1}>
                {rules.map((rule, index) => (
                    <RuleRow
                        key={`rule-${index}`}
                        rule={rule}
                        idPrefix={`guest_category_box_${boxIndex}_rule_${index}`}
                        disabled={disabled}
                        setRule={nextRule => {
                            const nextRules = [...rules]
                            nextRules[index] = nextRule
                            setRules(nextRules)
                        }}
                        onDuplicate={() => {
                            const nextRules = [...rules]
                            nextRules.splice(index + 1, 0, {...rule})
                            setRules(nextRules)
                        }}
                        onDelete={() => setRules(rules.filter((_, candidate) => candidate !== index))}
                    />
                ))}
            </Stack>

            <Stack direction={'row'} gap={1} sx={{mt: 1}}>
                <Button
                    size={'small'}
                    variant={'text'}
                    startIcon={<AddIcon/>}
                    disabled={disabled || rules.length >= GUEST_CATEGORY_MAX_RULES_PER_BOX}
                    onClick={() => setRules([...rules, newGuestCategoryRule()])}
                >
                    {t('Add rule')}
                </Button>
            </Stack>
        </Paper>
    )
}

//endregion Rule box

const GuestCategoryDetails = () => {

    //region State

    useTrackScreenVisit('system guest category details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const readOnly = useReadOnly()
    const queryClient = useQueryClient()
    const {categories: allGuestCategories} = useGuestCategories()
    const [saveError, setSaveError] = useState<string | null>(null)
    // The revision the API holds for the record on screen. The header's Undo re-saves an older snapshot of the
    // record through a stale render closure, so the value lives in a ref instead of in the entity it re-saves.
    const storedRevision = useRef<number | undefined>(undefined)

    const isNameTaken = (candidate: GuestCategory) => allGuestCategories.some(other =>
        other.id !== candidate.id && other.name.trim().toLocaleLowerCase() === candidate.name?.trim().toLocaleLowerCase()
    )

    const invalidateGuestCategories = () => {
        void queryClient.invalidateQueries([GUEST_CATEGORIES_QUERY_KEY])
        void queryClient.invalidateQueries(serverTableQueryKey('system-guest-categories'))
    }

    // A refused save comes back as a status without text; say what it means, the header's snackbar cannot.
    const persist = async (guestCategory: GuestCategory) => {
        setSaveError(null)
        try {
            return await GuestCategoryService.save(guestCategory)
        } catch (error) {
            const status = (error as {response?: {status?: number}} | undefined)?.response?.status
            setSaveError(status === 409
                ? t('This guest category changed. Reload it before saving.')
                : getErrorMessage(error))
            throw error
        }
    }

    const guestCategoryData = useEntity<GuestCategory>({
        get: async () => {
            if (id && id !== 'new') {
                // The catalog is one short list per organization, so there is no single-record endpoint.
                const stored = (await GuestCategoryService.list()).find(guestCategory => guestCategory.id === id)
                storedRevision.current = stored?.revision
                return stored
            } else {
                storedRevision.current = undefined
                return {
                    name: '',
                    filter: newGuestCategoryFilter()
                }
            }
        },
        create: persist,
        update: guestCategory => persist({...guestCategory, revision: storedRevision.current}),
        delete: (id) => GuestCategoryService.delete({id: [id]}),
        onDeleted: () => {
            invalidateGuestCategories()
            navigate('/system/guestCategories', {replace: true})
        },
        onSaved: (guestCategory, wasCreateAction) => {
            storedRevision.current = guestCategory.revision
            invalidateGuestCategories()
            if (wasCreateAction) {
                navigate(`/system/guestCategories/${guestCategory.id}`, {replace: true})
            }
        },
        isValid: (guestCategory) => {
            return !!guestCategory.name?.trim()
                && !isNameTaken(guestCategory)
                && isValidGuestCategoryFilter(guestCategory.filter)
        },
        cleanupBeforeSave: (guestCategory) => {
            return {
                ...guestCategory,
                name: guestCategory.name?.trim()
            }
        }
    }, id)

    const guestCategory = guestCategoryData.item
    const setGuestCategory = guestCategoryData.setItem
    const nameMissing = isRequiredTextMissing(guestCategoryData, guestCategory.name)
    const nameTaken = !nameMissing && isNameTaken(guestCategory)
    const boxes = guestCategory.filter?.filterGroups ?? []
    const disabled = readOnly || guestCategoryData.loading || guestCategoryData.saving

    //endregion State

    //region Handlers

    const updateField = (field: keyof GuestCategory, value: any) => {
        setGuestCategory({...guestCategory, [field]: value})
    }

    const setBoxes = (nextBoxes: FilterGroup[]) => {
        updateField('filter', {...guestCategory.filter, operator: DataFilterOperator.OR, filterGroups: nextBoxes})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={guestCategory.name || t('New guest category')}
            data={guestCategoryData}
            back={() => navigate('/system/guestCategories')}
        />
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label for this guest category in filters.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={guestCategory.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing || nameTaken}
                        helperText={nameTaken ? t('Another guest category already uses this name') : undefined}
                        inputProps={{maxLength: 100}}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const rulesSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Rules')}
            </Typography>

            <Alert severity={'info'} variant={'outlined'} sx={{bgcolor: 'transparent'}}>
                <Typography variant={'body1'}>
                    {t('Counts completed accommodation stays per guest. Nights count only after the stay has completed. Ongoing, future, cancelled and no-show stays are excluded.')}
                    {' '}
                    {t('A guest matches the category when all rules in any one box apply.')}
                </Typography>
            </Alert>

            <Stack gap={1.5}>
                {boxes.map((box, index) => (
                    <React.Fragment key={`box-${index}`}>
                        {index > 0 && (
                            <Typography
                                variant={'body2'}
                                align={'center'}
                                color={'success.main'}
                                sx={{fontWeight: 600, letterSpacing: '0.08em'}}
                            >
                                {t('OR — alternatively')}
                            </Typography>
                        )}
                        <RuleBox
                            box={box}
                            index={index}
                            disabled={disabled}
                            setBox={nextBox => {
                                const nextBoxes = [...boxes]
                                nextBoxes[index] = nextBox
                                setBoxes(nextBoxes)
                            }}
                            onEmpty={() => setBoxes(boxes.filter((_, candidate) => candidate !== index))}
                        />
                    </React.Fragment>
                ))}

                <Stack alignItems={'center'}>
                    <Button
                        variant={'text'}
                        color={'success'}
                        startIcon={<AddIcon/>}
                        disabled={disabled || boxes.length >= GUEST_CATEGORY_MAX_BOXES}
                        onClick={() => setBoxes([...boxes, newGuestCategoryBox()])}
                    >
                        {t(boxes.length > 0 ? 'OR box' : 'Add rule')}
                    </Button>
                </Stack>
            </Stack>

            <Alert severity={'info'} variant={'outlined'} sx={{bgcolor: 'transparent'}}>
                <Typography variant={'body1'}>
                    {t('A guest can match more than one category. Changes apply to new survey invitations; existing invitations keep their saved category results.')}
                </Typography>
            </Alert>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete guest category')}
                description={t('Deleting a guest category removes it from the filters. Survey invitations keep the category results saved when they were sent.')}
                buttonLabel={t('Delete')}
                loading={guestCategoryData.saving}
                onDelete={() => guestCategoryData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {guestCategory.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {saveError && (
                    <Alert severity={'error'}>{t(saveError)}</Alert>
                )}

                {guestCategoryData.isMissing ? (
                    <Alert severity={'warning'}>{t('Guest category not found.')}</Alert>
                ) : (
                    <>
                        {basicInfoSection}
                        <Divider/>
                        {rulesSection}

                        {
                            !!guestCategory.id &&
                            <>
                                <Divider/>
                                {dangerZoneSection}
                            </>
                        }
                    </>
                )}
            </Stack>
        </Box>
    );
}

export default GuestCategoryDetails;
